<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { api, ApiError } from '@/api/client';
import { useCategoriesStore } from '@/stores/categories';
import { useEventsStore } from '@/stores/events';
import type { EventItem } from '@/api/types';

// Reusable school-schedule editor used by both the Tips dialog and onboarding.
// The host renders the surrounding chrome (titles/buttons) and calls save().
const cats = useCategoriesStore();
const events = useEventsStore();

interface DayRow { key: string; label: string; date: string; enabled: boolean; start: string; end: string; }
const LABELS = ['Ma', 'Di', 'Wo', 'Do', 'Vr'];
const KEYS = ['mon', 'tue', 'wed', 'thu', 'fri'];

const days = ref<DayRow[]>([]);
const endDate = ref('');
const loading = ref(false);
const error = ref<string | null>(null);

function iso(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function mondayOfThisWeek() {
  const t = new Date(); t.setHours(0, 0, 0, 0);
  t.setDate(t.getDate() - ((t.getDay() + 6) % 7));
  return t;
}
function defaultJuly() {
  const t = new Date(); const y = t.getFullYear();
  let j = new Date(y, 6, 17);
  if (j.getTime() < t.getTime()) j = new Date(y + 1, 6, 17);
  return iso(j);
}
function weekdayIndex(dateIso: string) {
  return (new Date(`${dateIso}T00:00:00`).getDay() + 6) % 7;
}
function thisWeekRange() {
  const mon = mondayOfThisWeek();
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  return { from: iso(mon), to: iso(sun), mon };
}

async function schoolCategoryId(): Promise<string> {
  await cats.load();
  return cats.items.find((c) => c.slug === 'school')?.id
    ?? cats.items.find((c) => c.pointsPerHour === 0)?.id
    ?? cats.items[0]?.id
    ?? '';
}

async function load() {
  loading.value = true; error.value = null;
  const { from, to, mon } = thisWeekRange();
  const base: DayRow[] = LABELS.map((label, i) => {
    const d = new Date(mon); d.setDate(mon.getDate() + i);
    return { key: KEYS[i], label, date: iso(d), enabled: true, start: '08:30', end: '15:30' };
  });
  endDate.value = defaultJuly();
  try {
    const existing = await api<EventItem[]>('GET', `/api/events?from=${from}&to=${to}`);
    const school = existing.filter((e) => e.recurrenceFreq === 'weekly' && e.title === 'School' && e.seriesId);
    if (school.length > 0) {
      base.forEach((r) => { r.enabled = false; });
      for (const e of school) {
        const wd = weekdayIndex(e.date);
        if (wd >= 0 && wd < 5) { base[wd].enabled = true; base[wd].start = e.startTime; base[wd].end = e.endTime; }
        if (e.recurrenceEndDate) endDate.value = e.recurrenceEndDate;
      }
    }
  } catch { /* first run / offline → keep defaults */ }
  days.value = base; loading.value = false;
}
onMounted(load);

function valid(): boolean {
  error.value = null;
  const on = days.value.filter((d) => d.enabled);
  if (on.length === 0) { error.value = 'Kies minstens één schooldag.'; return false; }
  for (const d of on) if (d.end <= d.start) { error.value = `${d.label}: eindtijd moet na starttijd liggen.`; return false; }
  if (!endDate.value) { error.value = 'Kies een einddatum.'; return false; }
  if (days.value[0] && endDate.value < days.value[0].date) { error.value = 'Einddatum ligt vóór deze week.'; return false; }
  return true;
}

/** Replace the user's school series with the current selection. Returns
 *  true on success so the host can advance/close. */
async function save(): Promise<boolean> {
  if (!valid()) return false;
  try {
    const categoryId = await schoolCategoryId();
    const { from, to } = thisWeekRange();
    const existing = await api<EventItem[]>('GET', `/api/events?from=${from}&to=${to}`);
    const seen = new Set<string>();
    for (const e of existing) {
      if (e.recurrenceFreq === 'weekly' && e.title === 'School' && e.seriesId && !seen.has(e.seriesId)) {
        seen.add(e.seriesId);
        await api('DELETE', `/api/events/${e.id}?scope=all`);
      }
    }
    for (const d of days.value.filter((x) => x.enabled)) {
      await api('POST', '/api/events', {
        categoryId, title: 'School', date: d.date, startTime: d.start, endTime: d.end,
        recurrence: { freq: 'weekly', endDate: endDate.value },
      });
    }
    await events.reloadAll();
    return true;
  } catch (e) {
    error.value = e instanceof ApiError ? e.message : 'Opslaan mislukt.';
    return false;
  }
}

defineExpose({ save });
</script>

<template>
  <div>
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-3" />
    <div
      v-for="d in days"
      :key="d.key"
      class="d-flex align-center"
      style="gap: 8px; padding: 2px 0;"
    >
      <v-checkbox v-model="d.enabled" :label="d.label" density="compact" hide-details color="primary" style="flex: 0 0 76px;" />
      <v-text-field v-model="d.start" type="time" density="compact" variant="outlined" hide-details :disabled="!d.enabled" style="flex: 1;" />
      <span class="text-medium-emphasis">–</span>
      <v-text-field v-model="d.end" type="time" density="compact" variant="outlined" hide-details :disabled="!d.enabled" style="flex: 1;" />
    </div>
    <v-text-field
      v-model="endDate"
      type="date"
      label="Plan dit rooster tot"
      :min="days[0]?.date"
      variant="outlined"
      density="compact"
      hide-details
      class="mt-4"
    />
    <v-alert v-if="error" type="error" density="compact" class="mt-3" :text="error" />
  </div>
</template>
